/**
 * machinemotion.js
 * Gateway to our MachineMotion server
 * Notes:
 *   - the intent of this file is to define the assembler's interface with our MachineMotion,
 *     by gathering all that stuff in one place
 *
 * idea: this could be called API_machinemotion ?
 */


import { doAjax, getRemoteHost }  from './api_request.js';

const gErrorHandlers = [];
const gVersion = 'v1'; // TODO : Eventually have an handshake to retrieve highest supported version on both sides


function addErrorHandler(pHandler) {
  if(typeof pHandler == 'function') {
    gErrorHandlers.push(pHandler);
  }
}

function removeErrorHandler(pHandler) {
  const _lIndex = gErrorHandlers.indexOf(pHandler);
  if(_lIndex != -1) {
    _lIndex.splice(_lIndex, 1);
  }
}

function _errorHandler(pError) {
  console.error(pError);
}
addErrorHandler(_errorHandler);

function _onError(xhr, status, error) {
  let _lErrorMsg = 'MachineMotion ' + status + ' : ' + error + '\n';
  try {
    _lErrorMsg += JSON.parse(xhr.responseText).error;
    if(!_lErrorMsg) { _lErrorMsg = xhr.responseText; }
  }  catch(e) {
    _lErrorMsg += xhr.responseText;
  }
  gErrorHandlers.forEach(function(pHandler) {
    pHandler(_lErrorMsg);
  });
}

function isAvailable(pCompletion) {
  instructionSet(function(pData) {
    if(pCompletion) {
      pCompletion(pData != null);
    }
  });
}


function instructionSet(pCompletion) {
  return doAjax({
    url: (getRemoteHost() + "/" + gVersion + "/schema/instructionSet"),
    type: "GET",
    dataType: "json",
    success: function(_pData) { if(pCompletion) { pCompletion(_pData); } },
    error: function (/*xhr, status, error*/) { /*_onError(xhr, status, error);*/ if(pCompletion) { pCompletion(null); } }
  });
}

function library(pOnSuccess, pOnError) {
  return doAjax({
    url: (getRemoteHost() + "/" + gVersion + "/library"),
    type: "GET",
    dataType: "json",
    success: function(_pData) { if(pOnSuccess) { pOnSuccess(_pData); } },
    error: function (xhr, status, error) { /*_onError(xhr, status, error);*/ if(pOnError) { pOnError({xhr: xhr, status: status, error: error}); } }
  });
}

function getApplications(pOnSuccess, pOnError) {
  return doAjax({
    url: (getRemoteHost() + "/" + gVersion + "/library/applications"),
    type: "GET",
    dataType: "json",
    success: function(_pData) { if(pOnSuccess) { pOnSuccess(_pData); } },
    error: function (xhr, status, error) { /*_onError(xhr, status, error);*/ if(pOnError) { pOnError({xhr: xhr, status: status, error: error}); } }
  });
}

/**
 * Try and update the library to the latest version.
 *
 * @param {Object} pLibrary Library that needs patching.
 * @param {Function<Boolean, Object>} pCompletion First parameter is true if the patch occurred, the second parameter is the new Library JSON.
 * @returns Ajax Promise
 */
function patchLibrary(pLibrary, pCompletion) {
  return doAjax({
    url: (getRemoteHost() + "/" + gVersion + "/library/patch"),
    type: "POST",
    dataType: "json",
    data: JSON.stringify(pLibrary),
    contentType: "application/json",
    success: function(_pData) { if(pCompletion) { pCompletion(_pData.didUpgrade, _pData.library); } },
    error: function (xhr, status, error) { if(pCompletion) { pCompletion({xhr: xhr, status: status, error: error}); } }
  });
}

function application(pName, pCompletion) {
  return doAjax({
    url: (getRemoteHost() + "/" + gVersion + "/library/" + encodeURIComponent(pName)),
    type: "GET",
    dataType: "json",
    success: function(_pData) { if(pCompletion) { pCompletion(_pData); } },
    error: function (xhr, status, error) { _onError(xhr, status, error); if(pCompletion) { pCompletion(null); } }
  });
}

function deploy(pApp, pCompletion) {
  return doAjax({
    url: (getRemoteHost() + "/" + gVersion + "/library"),
    type: "POST",
    data: JSON.stringify(pApp),
    dataType: "json",
    contentType: "application/json",
    success: function(_pData) { if(pCompletion) { pCompletion(_pData); } },
    error: function(xhr, status, error) { _onError(xhr, status, error); if(pCompletion) { pCompletion(null); } }
  });
}

function update(pApp, pCompletion) {
  return doAjax({
    url: (getRemoteHost() + "/" + gVersion + "/library"),
    type: "PUT",
    data: JSON.stringify(pApp),
    dataType: "json",
    contentType: "application/json",
    success: function(_pData) { if(pCompletion) { pCompletion(_pData); } },
    error: function(xhr, status, error) { _onError(xhr, status, error); if(pCompletion) { pCompletion(null); } }
  });
}

function rename(pOldName, pApp, pCompletion) {
  return doAjax({
    url: (getRemoteHost() + "/" + gVersion + "/library/rename/" + encodeURIComponent(pOldName)),
    type: "PUT",
    data: JSON.stringify(pApp),
    dataType: "json",
    contentType: "application/json",
    success: function(_pData) { if(pCompletion) { pCompletion(_pData); } },
    error: function(xhr, status, error) { _onError(xhr, status, error); if(pCompletion) { pCompletion(null); } }
  });
}

function activateConfiguration( pCompletion) {
  return doAjax({
    url: (getRemoteHost() + "/" + gVersion + "/library/activateConfiguration"),
    type: "POST",
    success: function(_pData) { if(pCompletion) { pCompletion(_pData); } },
    error: function(xhr, status, error) { _onError(xhr, status, error); if(pCompletion) { pCompletion(null); } },
    contentType: "application/json"
  });
}

function remove(pApp, pCompletion) {
  return doAjax({
    url: (getRemoteHost() + "/" + gVersion + "/library/" + pApp.mId),
    type: "DELETE",
    data: JSON.stringify({ uuid : pApp.mUUID }),
    dataType: "json",
    contentType: "application/json",
    success: function(_pData) { if(pCompletion) { pCompletion(_pData); } },
    error: function(xhr, status, error) { _onError(xhr, status, error); if(pCompletion) { pCompletion(null); } }
  });
}

function play(pAppName, pRuntimeParams, pCompletion) {
  return doAjax({
    url: (getRemoteHost() + "/" + gVersion + "/launcher/" + encodeURIComponent(pAppName) + "/start"),
    type: "PUT",
    dataType: "json",
    success: function(_pData) { if(pCompletion) { pCompletion(_pData); } },
    error: function(xhr, status, error) { /*_onError(xhr, status, error);*/ if(pCompletion) { pCompletion(null); } },
    contentType: "application/json",
    data: JSON.stringify(pRuntimeParams || {})
  });
}

function playTemporary(pAppName, pAppJson, pRuntimeParams, pCompletion) {
  return doAjax({
    url: (getRemoteHost() + "/" + gVersion + "/launcher/" + encodeURIComponent(pAppName) + "/startTemporary"),
    type: "PUT",
    dataType: "json",
    success: function(_pData) { if(pCompletion) { pCompletion(_pData); } },
    error: function(xhr, status, error) { _onError(xhr, status, error); if(pCompletion) { pCompletion(null); } },
    contentType: "application/json",
    data: JSON.stringify({
      app: pAppJson,
      runtimeConfig: pRuntimeParams || {}
    })
  });
}

function auto_launch_on(pApp, pCompletion) {
  return doAjax({
    url: (getRemoteHost() + "/" + gVersion + "/launcher/" + encodeURIComponent(pApp) + "/autoLaunch/true"),
    type: "PUT",
    success: function(_pData) { if(pCompletion) { pCompletion(_pData); } },
    error: function(xhr, status, error) { _onError(xhr, status, error); if(pCompletion) { pCompletion(null); } }
  });
}

function auto_launch_off(pApp, pCompletion) {
  return doAjax({
    url: (getRemoteHost() + "/" + gVersion + "/launcher/" + encodeURIComponent(pApp) + "/autoLaunch/false"),
    type: "PUT",
    success: function(_pData) { if(pCompletion) { pCompletion(_pData); } },
    error: function(xhr, status, error) { _onError(xhr, status, error); if(pCompletion) { pCompletion(null); } }
  });
}

function pause(pAppName, pCompletion) {
  return doAjax({
    url: (getRemoteHost() + "/" + gVersion + "/launcher/" + encodeURIComponent(pAppName) + "/pause"),
    type: "PUT",
    success: function(_pData) { if(pCompletion) { pCompletion(_pData); } },
    error: function(xhr, status, error) { _onError(xhr, status, error); if(pCompletion) { pCompletion(null); } }
  });
}

function resume(pAppName, pCompletion) {
  return doAjax({
    url: (getRemoteHost() + "/" + gVersion + "/launcher/" + encodeURIComponent(pAppName) + "/resume"),
    type: "PUT",
    success: function(_pData) { if(pCompletion) { pCompletion(_pData); } },
    error: function(xhr, status, error) { _onError(xhr, status, error); if(pCompletion) { pCompletion(null); } }
  });
}

function stop(pAppName, pCompletion) {
  return doAjax({
    url: (getRemoteHost() + "/" + gVersion + "/launcher/" + encodeURIComponent(pAppName) + "/stop"),
    type: "PUT",
    success: function(_pData) { if(pCompletion) { pCompletion(_pData); } },
    error: function(xhr, status, error) { _onError(xhr, status, error); if(pCompletion) { pCompletion(null); } }
  });
}

function stopAllMotion(pCompletion) {
  return doAjax({
    url: (getRemoteHost() + "/" + gVersion + "/immediate/stop_all_motion"),
    type: "POST",
    success: function(_pData) { if(pCompletion) { pCompletion(_pData); } },
    error: function(xhr, status, error) { _onError(xhr, status, error); if(pCompletion) { pCompletion(null); } }
  });
}

function status(pCompletion) {
  return doAjax({
    url: (getRemoteHost() + "/" + gVersion + "/application"),
    type: "GET",
    success: function(_pData) { if(pCompletion) { pCompletion(_pData); } },
    error: function (xhr, status, error) { _onError(xhr, status, error); if(pCompletion) { pCompletion(null); } }
  });
}

function simulateInputDigitalPin(pController, pDevice, pPin, pValue, pCompletion) {
  return doAjax({
    url: (getRemoteHost() + "/" + gVersion + "/application/simulate_input_digital_pin/" + pController + '/' + pDevice + '/' + pPin + "/" + pValue),
    type: "PUT",
    success: function(_pData) { if(pCompletion) { pCompletion(_pData); } },
    error: function(xhr, status, error) { _onError(xhr, status, error); if(pCompletion) { pCompletion(null); } }
  });
}

function setVariable(pVariableName, pValue, pAppName, pCompletion) {
  return doAjax({
    url: (getRemoteHost() + "/" + gVersion + "/launcher/" + encodeURIComponent(pAppName) + "/setVariable"),
    type: "PUT",
    dataType: "json",
    success: function(_pData) { if(pCompletion) { pCompletion(_pData); } },
    error: function(xhr, status, error) { _onError(xhr, status, error); if(pCompletion) { pCompletion(null); } },
    contentType: "application/json",
    data: JSON.stringify({'variableName' : pVariableName, 'value' : pValue} || {})
  });
}

function generateEvent(pEventTopic, pEventMessage, pCompletion) {
  const lMessage = { eventTopic: pEventTopic, eventMessage: pEventMessage };

  return doAjax({
    url: (getRemoteHost() + "/" + gVersion + "/application/generate_event"),
    type: "PUT",
    dataType: "json",
    contentType: "application/json",
    data: JSON.stringify(lMessage),
    success: function(_pData) { if(pCompletion) { pCompletion(_pData); } },
    error: function(xhr, status, error) { _onError(xhr, status, error); if(pCompletion) { pCompletion(null); } }
  });
}


function getOnboardingStatus(pCompletion) {
  return doAjax({
    url: (getRemoteHost() + "/" + gVersion + "/configure/onboarding"),
    type: "GET",
    success: function(_pData) { if (pCompletion) { pCompletion(_pData); } },
    error: function (xhr, status, error) { _onError(xhr, status, error); if (pCompletion) { pCompletion(null); } }
  });
}

function setOnboardingStatus(value, pCompletion) {
  return doAjax({
    url: (getRemoteHost() + "/" + gVersion + "/configure/onboarding"),
    type: "POST",
    dataType: "json",
    contentType: "application/json",
    data: JSON.stringify(value),
    success: function(_pData) { if (pCompletion) { pCompletion(_pData); } },
    error: function (xhr, status, error) { _onError(xhr, status, error); if (pCompletion) { pCompletion(null); } }
  });
}

/**
 * API call to retrieve information about the MachineLogic configuration.
 * @param {requestCallback} pCompletion - The callback that handle the response
 * @returns {void}
 */
function getConfigInfo(pCompletion) {
  return doAjax({
    url: (getRemoteHost() + "/" + gVersion + "/configure/config_info"),
    type: "GET",
    success: function(_pData) { if (pCompletion) { pCompletion(_pData); } },
    error: function (xhr, status, error) { _onError(xhr, status, error); if (pCompletion) { pCompletion(null); } }
  });
}
/**
  * API call to retrieve the status of the config checker save from configchecker.
  * @param {requestCallback} pCompletion - The callback that handle the response
  * @returns {void}
  */
function getResultStatus(pControllerId, pAxisUuid, pCompletion) {
  return doAjax({
    url: (getRemoteHost() + "/" + gVersion + "/configChecker/getResultStatus?controllerId=" + encodeURIComponent(pControllerId) + "&axisUuid=" + encodeURIComponent(pAxisUuid)),
    type: "GET",
    dataType: "json",
    success: function(_pData) { if (pCompletion) { pCompletion(_pData); } },
    error: function (/*xhr, status, error*/) { /*_onError(xhr, status, error);*/ if (pCompletion) { pCompletion(null); } }
  });
}

/**
 * API call to retrieve conpatible axis types for config checker
 * @param {requestCallback} pCompletion - The callback that handle the response
 * @returns {void}
 */
function getCompatibleAxisTypes(pCompletion) {
  return doAjax({
    url: (getRemoteHost() + "/" + gVersion + "/configChecker/getCompatibleAxisTypes/"),
    type: "GET",
    dataType: "json",
    success: function(_pData) { if (pCompletion) { pCompletion(_pData); } },
    error: function (xhr, status, error) { _onError(xhr, status, error); if (pCompletion) { pCompletion(null); } }
  });
}

function getMachineAppLibrary(pCompletion) {
  return doAjax({
    url: (getRemoteHost() + "/" + gVersion + "/machineApp/library"),
    type: "GET",
    dataType: "json",
    success: function(_pData) { if (pCompletion) { pCompletion(_pData); } },
    error: function (xhr, status, error) { _onError(xhr, status, error); if (pCompletion) { pCompletion(null); } }
  });
}

// http://localhost:3100/v1/immediate/estop
/**
 * API call to check estop status
 * @param {requestCallback} pCompletion - The callback that handle the response
 * @returns {void}
 */
function getEstop(pCompletion) {
  return doAjax({
    url: (getRemoteHost() + "/" + gVersion + "/immediate/estop"),
    type: "GET",
    dataType: "json",
    success: function(_pData) { if (pCompletion) { pCompletion(_pData); } },
    error: function (xhr, status, error) { _onError(xhr, status, error); if (pCompletion) { pCompletion(null); } }
  });
}
/* Network */
function getDetectedNetwork(pCompletion) {
  return doAjax({
    url: (getRemoteHost() + "/" + gVersion + "/network/detected_network"),
    type: "GET",
    dataType: "json",
    success: function(_pData) { if (pCompletion) { pCompletion(_pData); } },
    error: function (xhr, status, error) { _onError(xhr, status, error); if (pCompletion) { pCompletion(null); } }
  });
}

function setPassword(pPasswordsTargets, pPassword, pPasswordConfirmation, pOldPassword, pCompletion){
  return doAjax({
    url: (getRemoteHost() + "/" + gVersion +  "/network/set_password"),
    type: "POST",
    dataType: "json",
    contentType: "application/json",
    data: JSON.stringify( {
      targets: pPasswordsTargets,
      password: pPassword,
      password_confirmation: pPasswordConfirmation,
      old_password: pOldPassword
    } ),
    success: function(_pData) { if (pCompletion) { pCompletion(_pData); } },
    error: function (xhr, status, error) { _onError(xhr, status, error); if (pCompletion) { pCompletion(null); } }
  });
}

function setMachineAppLibrary(pJson, pCompletion) {
  return doAjax({
    url: (getRemoteHost() + "/" + gVersion + "/machineApp/library"),
    type: "POST",
    dataType: "json",
    data: JSON.stringify(pJson),
    contentType: "application/json",
    success: function(_pData) { if(pCompletion) { pCompletion(true, _pData); } },
    error: function (xhr, status, error) { if(pCompletion) { pCompletion(false, {xhr: xhr, status: status, error: error}); } }
  });
}

function upsertMachineAppItem(pMachineAppType, pMachineAppItemType, pJson, pCompletion) {
  return doAjax({
    url: (getRemoteHost() + "/" + gVersion + "/machineApp/library/update/" + pMachineAppType + "/" + pMachineAppItemType),
    type: "POST",
    dataType: "json",
    data: JSON.stringify(pJson),
    contentType: "application/json",
    success: function(_pData) { if(pCompletion) { pCompletion(true, _pData); } },
    error: function (xhr, status, error) { if(pCompletion) { pCompletion(false, {xhr: xhr, status: status, error: error}); } }
  });
}

function deleteMachineAppItem(pMachineAppType, pMachineAppItemType, pUuid, pCompletion) {
  return doAjax({
    url: (getRemoteHost() + "/" + gVersion + "/machineApp/library/remove/" + pMachineAppType + "/" + pMachineAppItemType + "/" + pUuid),
    type: "DELETE",
    contentType: "application/json",
    success: function(_pData) { if(pCompletion) { pCompletion(true, _pData); } },
    error: function (xhr, status, error) { if(pCompletion) { pCompletion(false, {xhr: xhr, status: status, error: error}); } }
  });
}

function getConfiguredNetworkStatus(pCompletion) {
  return doAjax({
    url: (getRemoteHost() + "/" + gVersion + "/network/configured_network_status"),
    type: "GET",
    dataType: "json",
    success: function(_pData) { if (pCompletion) { pCompletion(_pData); } },
    error: function (xhr, status, error) { _onError(xhr, status, error); if (pCompletion) { pCompletion(null); } }
  });
}


function isHostControllerTheParent(pCompletion) {
  return doAjax({
    url: (getRemoteHost() + "/" + gVersion + "/network/is_host_controller_the_parent"),
    type: "GET",
    dataType: "json",
    success: function(_pData) { if (pCompletion) { pCompletion(_pData); } },
    error: function (xhr, status, error) { _onError(xhr, status, error); if (pCompletion) { pCompletion(null); } }
  });
}

/**
 * API call to play application through payload in request
 * url example: http://localhost:3100/v1/library/activate_by_payload
 * @param {requestCallback} pCompletion - The callback that handle the response
 * @returns {void}
 */
function airRunApplicationByPayload(pApp, pCompletion) {
  return doAjax({
    url: (getRemoteHost() + "/" + gVersion + "/library/activate_by_payload"),
    type: "PUT",
    dataType: "json",
    data: JSON.stringify(pApp),
    success: function(_pData) { if (pCompletion) { pCompletion(_pData); } },
    error: function (xhr, status, error) { _onError(xhr, status, error); if (pCompletion) { pCompletion(null); } }
  });
}

// http://localhost:3100/v1/library/activate_by_id/19413123
/**
 * API call to play application through payload in request
 * @param {requestCallback} pCompletion - The callback that handle the response
 * @returns {void}
 */
function checkRemoteAccess(pCompletion){
  return doAjax({
    url: (getRemoteHost() + "/check_machine_cloud_remote_access"),
    type: "POST",
    dataType: "json",
    success: function(_pData) { if (pCompletion) { pCompletion(_pData); } },
    error: function (xhr, status, error) { _onError(xhr, status, error); if (pCompletion) { pCompletion(null); } }
  });
}

function getApiVersion() {
  return gVersion;
}

/* Module export glue */
export {
  addErrorHandler,
  removeErrorHandler,
  instructionSet,
  isAvailable,
  getConfigInfo,
  library,
  getApplications,
  patchLibrary,
  application,
  deploy,
  update,
  activateConfiguration,
  rename,
  remove,
  play,
  playTemporary,
  auto_launch_on,
  auto_launch_off,
  simulateInputDigitalPin,
  setVariable,
  generateEvent,
  pause,
  resume,
  stop,
  stopAllMotion,
  status,
  getResultStatus,
  getCompatibleAxisTypes,
  airRunApplicationByPayload,
  getMachineAppLibrary,
  setMachineAppLibrary,
  upsertMachineAppItem,
  deleteMachineAppItem,
  getDetectedNetwork,
  getConfiguredNetworkStatus,
  isHostControllerTheParent,
  setPassword,
  getEstop,
  getOnboardingStatus,
  setOnboardingStatus,
  checkRemoteAccess,
  getApiVersion,
};
